import React, { useState } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const TermsAcceptance = ({ accepted, onChange, legalAcknowledgments, onLegalChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleCheckboxChange = (field, checked) => {
    onLegalChange({ ...legalAcknowledgments, [field]: checked });
    
    // Update overall accepted status based on all checkboxes
    const updatedAcknowledgments = { ...legalAcknowledgments, [field]: checked };
    const allAccepted = 
      updatedAcknowledgments?.acknowledgeMarketplaceOnly &&
      updatedAcknowledgments?.acknowledgeNoInsurance &&
      updatedAcknowledgments?.agreeRentalAgreement &&
      updatedAcknowledgments?.agreeArbitrationWaiveClassAction;
    
    onChange(allAccepted);
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-3 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="FileText" size={20} color="var(--color-primary)" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl font-semibold text-foreground">
          Terms & Conditions
        </h2>
      </div>

      <div className="space-y-4">
        <div className="border border-border rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full flex items-center justify-between p-4 bg-muted hover:bg-muted/80 transition-colors"
          >
            <span className="font-body text-base font-medium text-foreground">
              View Rental Agreement & Terms
            </span>
            <Icon
              name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
              size={20}
              strokeWidth={2}
            />
          </button>

          {isExpanded && (
            <div className="p-4 bg-background border-t border-border max-h-96 overflow-y-auto">
              <div className="space-y-4 font-body text-sm text-muted-foreground">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">1. Rental Agreement</h3>
                  <p>
                    By completing this booking, you agree to rent the trailer for the specified dates and times. The renter must be at least 25 years old with a valid driver's license and insurance.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">2. Payment Terms</h3>
                  <p>
                    Full payment is required at the time of booking. The security deposit will be held and refunded within 7 business days after the trailer is returned in good condition. Cleaning fees are non-refundable.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">3. Cancellation Policy</h3>
                  <p>
                    Cancellations made 7+ days before pickup receive a full refund minus processing fees. Cancellations within 7 days are non-refundable. The security deposit is always refundable regardless of cancellation timing.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">4. Damage & Liability</h3>
                  <p>
                    The renter is responsible for any damage to the trailer during the rental period. The security deposit may be used to cover repair costs. Renters must maintain proper insurance coverage during the rental period.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">5. Usage Restrictions</h3>
                  <p>
                    Trailers must be used for their intended purpose only. Prohibited uses include illegal activities, subleasing, or exceeding weight capacity. Violations may result in immediate termination of the rental and forfeiture of deposits.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">6. Return Conditions</h3>
                  <p>
                    Trailers must be returned clean and in the same condition as received. Late returns incur additional daily charges. Failure to return the trailer may result in theft charges and legal action.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">7. Insurance Coverage</h3>
                  <p>
                    Basic insurance coverage is included in the rental fee. This covers major structural damage but does not include negligence, intentional damage, or theft. Additional coverage options are available.
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold text-foreground mb-2">8. Dispute Resolution</h3>
                  <p>
                    Any disputes arising from this rental agreement will be resolved through binding arbitration in accordance with the laws of the state of Texas. Both parties agree to waive the right to a jury trial.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Legal Acknowledgment Checkboxes */}
        <div className="space-y-3 pt-4 border-t border-border">
          <h3 className="font-heading text-lg font-semibold text-foreground mb-3">
            Required Acknowledgments
          </h3>
          
          <Checkbox
            id="acknowledge-marketplace"
            checked={legalAcknowledgments?.acknowledgeMarketplaceOnly || false}
            onChange={(e) => handleCheckboxChange('acknowledgeMarketplaceOnly', e?.target?.checked)}
            label="I acknowledge Draggin Trailers LLC is a marketplace only"
            required
          />

          <Checkbox
            id="acknowledge-no-insurance"
            checked={legalAcknowledgments?.acknowledgeNoInsurance || false}
            onChange={(e) => handleCheckboxChange('acknowledgeNoInsurance', e?.target?.checked)}
            label="I acknowledge Draggin Trailers LLC does NOT provide insurance"
            required
          />

          <Checkbox
            id="agree-rental-agreement"
            checked={legalAcknowledgments?.agreeRentalAgreement || false}
            onChange={(e) => handleCheckboxChange('agreeRentalAgreement', e?.target?.checked)}
            label="I agree to the Owner–Renter Rental Agreement"
            required
          />

          <Checkbox
            id="agree-arbitration"
            checked={legalAcknowledgments?.agreeArbitrationWaiveClassAction || false}
            onChange={(e) => handleCheckboxChange('agreeArbitrationWaiveClassAction', e?.target?.checked)}
            label="I agree to binding arbitration and waive class actions"
            required
          />
        </div>

        <div className="flex items-start space-x-2 p-4 bg-warning/10 border border-warning/20 rounded-md">
          <Icon name="AlertTriangle" size={18} color="var(--color-warning)" strokeWidth={2} />
          <p className="font-body text-sm text-muted-foreground">
            All acknowledgments above are required to complete your booking. Please review each statement carefully before proceeding.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptance;